/**
 * Utilitário para obter o UID (UUID) do usuário autenticado via Keycloak,
 * para uso em RPCs e operações do Supabase que esperam auth_id / user identifier.
 *
 * Não usamos mais sessão do Supabase - todos os usuários autenticam via Keycloak.
 * Este utilitário obtém o uid a partir do token JWT (sub) ou da tabela users.
 */

import { supabase } from '@/integrations/supabase/client';
import { getStoredAccessToken, decodeJwtPayload } from './keycloakRoles';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface GetUidParams {
  /** Email do perfil (para fallback na tabela users) */
  profileEmail: string;
}

/**
 * Retorna o UID (UUID) do usuário para passar em RPCs do Supabase.
 * Ordem: sub do JWT (se UUID) → auth_id da tabela users (por email).
 *
 * @throws Error se não encontrar identificador válido
 */
export async function getKeycloakUidForSupabase({
  profileEmail,
}: GetUidParams): Promise<string> {
  const token = getStoredAccessToken();
  const payload = token ? decodeJwtPayload(token) : null;
  const sub = payload?.sub;

  if (sub && typeof sub === 'string' && UUID_REGEX.test(sub)) {
    return sub;
  }

  const { data: userRow } = await supabase
    .from('users')
    .select('auth_id')
    .eq('email', profileEmail)
    .maybeSingle();

  if (userRow?.auth_id) {
    return userRow.auth_id;
  }

  throw new Error('Usuário não autenticado. Faça login novamente.');
}

/**
 * Retorna o userId para namespacing de cache (ex: query persister).
 * Não lança erro - retorna null se não houver token/uid.
 */
export async function getKeycloakUserIdOrNull(): Promise<string | null> {
  try {
    const token = getStoredAccessToken();
    const payload = token ? decodeJwtPayload(token) : null;
    const sub = payload?.sub;
    if (sub && typeof sub === 'string' && UUID_REGEX.test(sub)) {
      return sub;
    }
    const email = payload?.email;
    if (email && typeof email === 'string') {
      const { data } = await supabase
        .from('users')
        .select('auth_id')
        .eq('email', email)
        .maybeSingle();
      if (data?.auth_id) return data.auth_id;
    }
    return sub && typeof sub === 'string' ? sub : null;
  } catch {
    return null;
  }
}

/**
 * Obtém uid a partir do token (email vem do payload).
 * Para uso em hooks que não têm acesso ao profile.
 */
export async function getKeycloakUidFromToken(): Promise<string> {
  const token = getStoredAccessToken();
  const payload = token ? decodeJwtPayload(token) : null;
  const email = payload?.email;
  if (!email || typeof email !== 'string') {
    throw new Error('Usuário não autenticado. Faça login novamente.');
  }
  return getKeycloakUidForSupabase({ profileEmail: email });
}
